import React, { useState, useRef } from 'react';
import { config } from '../config';
import { Link } from 'react-router-dom';
import HubLogoMark from './shared/HubLogoMark';
import './LandingPage.css';

const PMS_OPTIONS = ['Opera', 'Protel', 'Infor HMS', 'Shiji', 'Agilysys', 'OHIP', 'Other'];
const HOTEL_COUNTS = ['1 – 5', '6 – 20', '21 – 50', '50+'];

const PRODUCTS = [
  {
    id: 'vault',
    color: '#f59e0b',
    bg: 'rgba(245,158,11,0.08)',
    border: 'rgba(245,158,11,0.25)',
    title: 'Vault',
    tagline: 'Folios (and more) from your old PMS secured and available forever.',
    body: 'Complete encrypted cloud backup of your property management system. Retrieve any document, meet any audit, and satisfy any regulator — years into the future.',
    points: ['Automated PMS data backup', 'On-demand folio & document issuance', 'GDPR & fiscal regulation compliant'],
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
        <path d="M7 11V7a5 5 0 0110 0v4"/>
        <circle cx="12" cy="16" r="1" fill="#f59e0b"/>
      </svg>
    ),
  },
  {
    id: 'sync',
    color: '#06b6d4',
    bg: 'rgba(6,182,212,0.08)',
    border: 'rgba(6,182,212,0.25)',
    title: 'Sync',
    tagline: 'Migrate to a new PMS.',
    body: 'Migrate your data and evaluate parity between legacy and new systems from a single control panel.',
    points: ['Opera, OHIP, PEP, Protel and more', 'Custom field mapping & conversions', 'Real-time monitoring & alerts'],
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#06b6d4" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 12a8 8 0 018-8v2m8 6a8 8 0 01-8 8v-2"/>
        <path d="M20 4l-2 2m2-2l-2-2M4 20l2-2m-2 2l2 2"/>
      </svg>
    ),
  },
  {
    id: 'apps',
    color: '#4F7FFF',
    bg: 'rgba(79,127,255,0.08)',
    border: 'rgba(79,127,255,0.25)',
    title: 'Apps',
    tagline: 'Power tools for your operations.',
    body: 'Purpose-built hospitality apps that plug directly into your PMS. Start with Payment Porter to automate payment routing across every property.',
    points: ['Payment Porter for payment routing', 'Configurable rules per property', 'Run history & audit logs built in'],
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#4F7FFF" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7" rx="1.5"/>
        <rect x="14" y="3" width="7" height="7" rx="1.5"/>
        <rect x="3" y="14" width="7" height="7" rx="1.5"/>
        <rect x="14" y="14" width="7" height="7" rx="1.5"/>
      </svg>
    ),
  },
];

const STEPS = [
  {
    num: '01',
    title: 'Choose your products',
    body: 'Pick the Hub products your hotels need — Vault, Sync, Apps, or all three. Start small and expand as you grow.',
  },
  {
    num: '02',
    title: 'Connect your PMS',
    body: 'We configure secure connectors to your property management system. No PMS downtime, no disruption to operations.',
  },
  {
    num: '03',
    title: 'Go live and grow',
    body: 'Monitor everything from one portal. Add new properties, new products, and new integrations whenever you need.',
  },
];

export default function LandingPage() {
  const signupRef = useRef(null);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', company: '',
    pms: '', hotels: '',
  });
  const [errors, setErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const scrollToSignup = () => {
    signupRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const set = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const validateStep1 = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = true;
    if (!form.lastName.trim()) e.lastName = true;
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = true;
    if (!form.company.trim()) e.company = true;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateStep2 = () => {
    const e = {};
    if (!form.pms) e.pms = true;
    if (!form.hotels) e.hotels = true;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = async () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
    } else if (step === 2 && validateStep2()) {
      setSubmitLoading(true);
      setSubmitError(null);
      try {
        const res = await fetch(`${config.apiBaseUrl}/prospects`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            firstName: form.firstName,
            lastName:  form.lastName,
            email:     form.email,
            company:   form.company,
            pms:       form.pms,
            hotels:    form.hotels,
          }),
        });
        if (!res.ok) throw new Error('Submission failed');
        setStep(3);
      } catch {
        setSubmitError('Something went wrong. Please try again.');
      } finally {
        setSubmitLoading(false);
      }
    }
  };

  const dotState = (i) => {
    if (step > i) return 'done';
    if (step === i) return 'active';
    return '';
  };

  return (
    <div className="lp-root">

      {/* ── Nav ── */}
      <nav className="lp-nav">
        <div className="lp-nav-logo">
          <HubLogoMark size={32} />
          <span className="lp-nav-logo-text"><span>Hub</span> by HConnect</span>
        </div>
        <div className="lp-nav-actions">
          <Link to="/login" className="lp-btn-login">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10 14h3a1 1 0 001-1V3a1 1 0 00-1-1h-3M6 11l3-3-3-3M2 8h7"/>
            </svg>
            Log In
          </Link>
          <button className="lp-btn-nav-cta" onClick={scrollToSignup}>
            Get Started
          </button>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="lp-hero">
        <div className="lp-hero-bg">
          <div className="lp-hero-grid"/>
          <div className="lp-hero-glow-1"/>
          <div className="lp-hero-glow-2"/>
        </div>

        <div className="lp-hero-content">
          <div className="lp-hero-text">
            <div className="lp-badge">
              <div className="lp-badge-dot"/>
              Hotel Integration Platform
            </div>
            <h1 className="lp-hero-h1">
              Your hotel operations,<br/>
              <em>connected.</em>
            </h1>
            <p className="lp-hero-sub">
              Hub brings together data backup, system sync, and purpose-built hospitality apps in one platform — so your team spends less time managing systems and more time serving guests.
            </p>
            <div className="lp-hero-product-pills">
              {PRODUCTS.map(p => (
                <span key={p.id} className="lp-hero-pill" style={{ background: p.bg, border: `1px solid ${p.border}`, color: p.color }}>
                  {p.title}
                </span>
              ))}
            </div>
            <div className="lp-hero-cta">
              <button className="lp-btn-primary" onClick={scrollToSignup}>
                Get Started
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 8h10M9 4l4 4-4 4"/>
                </svg>
              </button>
              <a href="#products" className="lp-btn-secondary">
                See what's included
              </a>
            </div>
          </div>

          <div className="lp-hero-visual">
            <div className="lp-hub-cards">
              {PRODUCTS.map((p, i) => (
                <div key={p.id} className="lp-hub-mini-card" style={{ '--card-delay': `${i * 0.1}s` }}>
                  <div className="lp-hub-mini-icon" style={{ background: p.bg, border: `1px solid ${p.border}` }}>
                    {p.icon}
                  </div>
                  <div className="lp-hub-mini-body">
                    <div className="lp-hub-mini-title" style={{ color: p.color }}>{p.title}</div>
                    <div className="lp-hub-mini-tagline">{p.tagline}</div>
                  </div>
                  <div className="lp-hub-mini-dot" style={{ background: p.color }}/>
                </div>
              ))}
              <div className="lp-float-badge badge-1">
                <div className="lp-float-badge-icon" style={{ background: 'rgba(16,185,129,0.1)' }}>
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M13 4L6 11L3 8"/>
                  </svg>
                </div>
                <span style={{ color: '#059669' }}>3 products, 1 platform</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Stats Bar ── */}
      <div className="lp-stats-bar">
        <div className="lp-stats-inner">
          {[
            { val: '500', unit: 'M+', label: 'Records backed up' },
            { val: '99.99', unit: '%',  label: 'Uptime SLA' },
            { val: '25',  unit: '+',   label: 'Years data retention' },
            { val: '10',  unit: '+',   label: 'PMS connectors' },
          ].map((s) => (
            <div key={s.label}>
              <div className="lp-stat-item-val">{s.val}<span>{s.unit}</span></div>
              <div className="lp-stat-item-label">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Products ── */}
      <div className="lp-section" id="products">
        <div className="lp-section-label">What's included</div>
        <h2 className="lp-section-h2">Three products.<br/>One platform.</h2>
        <p className="lp-section-sub">
          Start with the product your hotels need most. Add the others as your operations grow.
        </p>
        <div className="lp-features-grid">
          {PRODUCTS.map((p) => (
            <div className="lp-feature-card lp-product-card" key={p.id}>
              <div className="lp-feature-icon-wrap" style={{ background: p.bg, border: `1px solid ${p.border}` }}>
                {p.icon}
              </div>
              <div className="lp-product-card-title" style={{ color: p.color }}>{p.title}</div>
              <div className="lp-feature-title">{p.tagline}</div>
              <div className="lp-feature-body">{p.body}</div>
              <ul className="lp-product-points">
                {p.points.map(pt => (
                  <li key={pt} className="lp-product-point">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke={p.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10 3L5 8.5 2 5.5"/>
                    </svg>
                    {pt}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* ── How It Works ── */}
      <div className="lp-how-wrapper" id="how-it-works">
        <div className="lp-section">
          <div className="lp-section-label">How it works</div>
          <h2 className="lp-section-h2">Up and running<br/>in days, not months</h2>
          <p className="lp-section-sub">
            Our team handles the integration end-to-end. You approve the connection, we do the rest.
          </p>
          <div className="lp-steps">
            {STEPS.map((s) => (
              <div className="lp-step" key={s.num}>
                <div className="lp-step-num">
                  <div className="lp-step-num-inner">{s.num}</div>
                </div>
                <div className="lp-step-title">{s.title}</div>
                <div className="lp-step-body">{s.body}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Sign Up ── */}
      <div className="lp-signup-wrapper" ref={signupRef} id="signup">
        <div className="lp-signup-inner">
          <div className="lp-signup-pitch">
            <div className="lp-section-label">Get started</div>
            <h2>Start with Hub today</h2>
            <p>
              Tell us about your setup and a Hub specialist will reach out within one business day to walk you through onboarding.
            </p>
            <ul className="lp-pitch-points">
              {[
                'No long-term contract required',
                'Dedicated onboarding engineer',
                'Live within 5 business days',
                'GDPR & fiscal regulation compliant',
              ].map((pt) => (
                <li className="lp-pitch-point" key={pt}>
                  <div className="lp-pitch-check">
                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="#4F7FFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10 3L5 8.5 2 5.5"/>
                    </svg>
                  </div>
                  {pt}
                </li>
              ))}
            </ul>
          </div>

          <div className="lp-form-card">
            <div className="lp-form-progress">
              <div className={`lp-form-step-dot ${dotState(1)}`}/>
              <div className={`lp-form-step-dot ${dotState(2)}`}/>
              <div className={`lp-form-step-dot ${dotState(3)}`}/>
            </div>

            {step < 3 && (
              <div className="lp-form-step-label">
                Step <strong>{step} of 2</strong> — {step === 1 ? 'Your details' : 'Your setup'}
              </div>
            )}

            {step === 1 && (
              <>
                <div className="lp-form-title">Tell us about yourself</div>
                <div className="lp-form-grid">
                  <div className="lp-form-field">
                    <label>First name</label>
                    <input type="text" value={form.firstName} onChange={set('firstName')} placeholder="Jane" style={errors.firstName ? { borderColor: '#f87171' } : {}}/>
                  </div>
                  <div className="lp-form-field">
                    <label>Last name</label>
                    <input type="text" value={form.lastName} onChange={set('lastName')} placeholder="Smith" style={errors.lastName ? { borderColor: '#f87171' } : {}}/>
                  </div>
                  <div className="lp-form-field">
                    <label>Work email</label>
                    <input type="email" value={form.email} onChange={set('email')} placeholder="jane@yourhotel.com" style={errors.email ? { borderColor: '#f87171' } : {}}/>
                  </div>
                  <div className="lp-form-field">
                    <label>Company / Chain</label>
                    <input type="text" value={form.company} onChange={set('company')} placeholder="Grand Palace Hotels" style={errors.company ? { borderColor: '#f87171' } : {}}/>
                  </div>
                </div>
                <div className="lp-form-actions">
                  <button className="lp-btn-submit" onClick={handleNext}>
                    Continue
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 8h10M9 4l4 4-4 4"/>
                    </svg>
                  </button>
                </div>
              </>
            )}

            {step === 2 && (
              <>
                <div className="lp-form-title">Your current PMS setup</div>
                <div className="lp-form-field" style={{ marginBottom: '20px' }}>
                  <label>Which PMS system do you use?</label>
                  <div className="lp-pms-grid" style={{ marginTop: '8px' }}>
                    {PMS_OPTIONS.map((pms) => (
                      <button key={pms} className={`lp-pms-option ${form.pms === pms ? 'selected' : ''}`} onClick={() => setForm((f) => ({ ...f, pms }))} style={errors.pms && !form.pms ? { borderColor: '#f87171' } : {}}>
                        {pms}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="lp-form-grid full">
                  <div className="lp-form-field">
                    <label>How many properties?</label>
                    <select value={form.hotels} onChange={set('hotels')} style={errors.hotels ? { borderColor: '#f87171' } : {}}>
                      <option value="">Select number of properties</option>
                      {HOTEL_COUNTS.map((h) => (
                        <option key={h} value={h}>{h} properties</option>
                      ))}
                    </select>
                  </div>
                </div>
                {submitError && <div style={{ color: '#f87171', fontSize: '13px', marginBottom: '12px' }}>{submitError}</div>}
                <div className="lp-form-actions" style={{ justifyContent: 'space-between' }}>
                  <button className="lp-btn-secondary" style={{ padding: '12px 20px', fontSize: '14px' }} onClick={() => { setStep(1); setErrors({}); setSubmitError(null); }} disabled={submitLoading}>
                    Back
                  </button>
                  <button className="lp-btn-submit" onClick={handleNext} disabled={submitLoading}>
                    {submitLoading ? 'Submitting…' : 'Submit'}
                    {!submitLoading && (
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 8h10M9 4l4 4-4 4"/>
                      </svg>
                    )}
                  </button>
                </div>
              </>
            )}

            {step === 3 && (
              <div className="lp-thankyou">
                <div className="lp-thankyou-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6L9 17l-5-5"/>
                  </svg>
                </div>
                <h3>You're on the list, {form.firstName}.</h3>
                <p>
                  A Hub specialist will be in touch within one business day to discuss your {form.hotels} {form.hotels === '1 – 5' ? 'properties' : 'properties'} on {form.pms}.
                  <br/><br/>
                  In the meantime, you can <Link to="/login" style={{ color: 'var(--blue-bright)', textDecoration: 'none' }}>log in</Link> if you already have an account.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Footer ── */}
      <footer>
        <div className="lp-footer">
          <span className="lp-footer-copy">© {new Date().getFullYear()} hconnect. All rights reserved.</span>
          <div className="lp-footer-links">
            <Link to="/login" className="lp-footer-link">Log In</Link>
          </div>
        </div>
      </footer>

    </div>
  );
}
